// Local
import { ReviewFilingSummaryWithStatus } from '@feature/crl/clerk-review-list-data.service';

/**
 * Clerk-edited document identity + title from a saved Clerk Review filing.
 */
export interface ClerkEditedDocumentTitle {
  documentId?: number | null;
  title?: string | null;
}

/** sessionStorage key for clerk-edited list titles (survives a same-tab refresh). */
export const CLERK_REVIEW_DOCUMENT_TITLE_OVERRIDES_KEY =
  'fsx.clerk-review-list.document-title-overrides';

/**
 * Serializes filing → documentId → title maps for sessionStorage.
 */
export function serializeDocumentTitleOverrides(
  overrides: ReadonlyMap<string, ReadonlyMap<number, string>>,
): string {
  const json: Record<string, Record<string, string>> = {};
  for (const [filingUniqueId, titles] of overrides) {
    const filing: Record<string, string> = {};
    for (const [documentId, title] of titles) {
      filing[String(documentId)] = title;
    }
    json[filingUniqueId] = filing;
  }
  return JSON.stringify(json);
}

/**
 * Restores filing → documentId → title maps from sessionStorage JSON.
 */
export function deserializeDocumentTitleOverrides(
  raw: string | null,
): Map<string, Map<number, string>> {
  const result = new Map<string, Map<number, string>>();
  if (!raw) {
    return result;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return result;
    }

    for (const [filingUniqueId, titles] of Object.entries(
      parsed as Record<string, unknown>,
    )) {
      if (!titles || typeof titles !== 'object' || Array.isArray(titles)) {
        continue;
      }

      const titlesByDocumentId = new Map<number, string>();
      for (const [documentId, title] of Object.entries(
        titles as Record<string, unknown>,
      )) {
        const id = Number(documentId);
        if (!Number.isFinite(id) || typeof title !== 'string') {
          continue;
        }
        titlesByDocumentId.set(id, title);
      }

      if (titlesByDocumentId.size > 0) {
        result.set(filingUniqueId, titlesByDocumentId);
      }
    }
  } catch {
    return new Map();
  }

  return result;
}

/**
 * Loads persisted clerk-edited titles. Private-mode storage failures yield an empty map.
 */
export function readDocumentTitleOverridesFromSession(
  storage: Pick<Storage, 'getItem'> = sessionStorage,
): Map<string, Map<number, string>> {
  try {
    return deserializeDocumentTitleOverrides(
      storage.getItem(CLERK_REVIEW_DOCUMENT_TITLE_OVERRIDES_KEY),
    );
  } catch {
    return new Map();
  }
}

/**
 * Persists clerk-edited titles so a same-tab refresh still overlays a stale query.
 * An empty map removes the key so a later login in the same tab cannot inherit titles.
 */
export function writeDocumentTitleOverridesToSession(
  overrides: ReadonlyMap<string, ReadonlyMap<number, string>>,
  storage: Pick<Storage, 'setItem' | 'removeItem'> = sessionStorage,
): void {
  try {
    if (overrides.size === 0) {
      storage.removeItem(CLERK_REVIEW_DOCUMENT_TITLE_OVERRIDES_KEY);
      return;
    }

    storage.setItem(
      CLERK_REVIEW_DOCUMENT_TITLE_OVERRIDES_KEY,
      serializeDocumentTitleOverrides(overrides),
    );
  } catch {
    // Quota / private mode — in-memory overrides still apply for this session.
  }
}

/**
 * Removes overlays for documents on the current page whose query `documentTitle`
 * already matches the stored title. Filings not on this page keep their overlays.
 *
 * @returns true when the override map changed.
 */
export function dropDocumentTitleOverridesCaughtUpOnServer(
  overrides: Map<string, Map<number, string>>,
  summaries: readonly ReviewFilingSummaryWithStatus[],
): boolean {
  const filingIdsOnPage = new Set<string>();
  const summariesByFilingId = new Map<string, ReviewFilingSummaryWithStatus>();
  for (const summary of summaries) {
    if (!summary.filingUniqueId) {
      continue;
    }
    filingIdsOnPage.add(summary.filingUniqueId);
    summariesByFilingId.set(summary.filingUniqueId, summary);
  }

  let changed = false;
  for (const filingUniqueId of [...overrides.keys()]) {
    if (!filingIdsOnPage.has(filingUniqueId)) {
      continue;
    }

    const titles = overrides.get(filingUniqueId);
    const summary = summariesByFilingId.get(filingUniqueId);
    if (!titles || !summary) {
      continue;
    }

    const documentIdsToDrop: number[] = [];
    for (const [documentId, storedTitle] of titles) {
      const document = summary.documents?.find(
        doc => doc.documentId === documentId,
      );
      if (document?.documentTitle === storedTitle) {
        documentIdsToDrop.push(documentId);
      }
    }

    for (const documentId of documentIdsToDrop) {
      titles.delete(documentId);
      changed = true;
    }

    if (titles.size === 0) {
      overrides.delete(filingUniqueId);
      changed = true;
    }
  }

  return changed;
}

/**
 * Copies clerk-edited document titles onto the matching list-row documents.
 * List rows store `documentTitle`; the detail save persists `title`.
 */
export function applyClerkEditedTitlesToSummaries(
  summaries: ReviewFilingSummaryWithStatus[],
  filingUniqueId: string,
  documents: readonly ClerkEditedDocumentTitle[],
): ReviewFilingSummaryWithStatus[] {
  const titlesByDocumentId = new Map<number, string>();
  for (const document of documents) {
    if (document.documentId == null || document.title == null) {
      continue;
    }
    titlesByDocumentId.set(document.documentId, document.title);
  }

  if (titlesByDocumentId.size === 0) {
    return summaries;
  }

  return summaries.map(summary => {
    if (summary.filingUniqueId !== filingUniqueId) {
      return summary;
    }

    return {
      ...summary,
      documents: summary.documents?.map(doc => {
        if (doc.documentId == null) {
          return doc;
        }
        const documentTitle = titlesByDocumentId.get(doc.documentId);
        return documentTitle === undefined ? doc : { ...doc, documentTitle };
      }),
    };
  });
}
